import styled, { css } from 'styled-components';
import { Icon, Modal } from 'src/components';
import {
  ButtonWrapper,
  MicIconWrapper as PermissionMicIcon,
} from 'src/apps/Buddy/PronunciationBuddy/PronunciationViews/MicPermissionsView/styles';

const overlayTextShadow = css`
  text-shadow: 0 1px 3px rgb(0 17 34 / 60%);
`;

export const Prompt = styled.button<{ $overlay?: boolean }>(
  ({ theme, $overlay }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.sz.s4};
    width: 100%;
    max-width: ${$overlay ? 'none' : theme.sz.sm};
    padding: ${$overlay ? 0 : theme.sz.s4};
    border: none;
    border-radius: ${theme.bo.medium};
    text-align: ${$overlay ? 'right' : 'left'};
    cursor: pointer;
    background: none;

    ${
      $overlay
        ? css`
            justify-content: flex-end;
            margin-bottom: 0;

            @media (max-width: ${theme.sz.md}) {
              gap: ${theme.sz.s3};
            }
          `
        : css`
            background-color: ${theme.co.tertiary98};

            &:hover {
              background-color: ${theme.co.tertiary95};
            }
          `
    }
  `,
);

export const MicIconWrapper = styled.div<{ $overlay?: boolean }>(
  ({ theme, $overlay }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: ${$overlay ? theme.sz.s24 : theme.sz.s16};
    height: ${$overlay ? theme.sz.s24 : theme.sz.s16};
    border-radius: ${theme.bo.round};
    order: ${$overlay ? 1 : 0};

    ${
      $overlay
        ? css`
            background-color: ${theme.co.primary20};
            color: ${theme.co.secondary0};
            font-size: ${theme.sz.s12};
            box-shadow: 0 ${theme.sz.s1} ${theme.sz.s4} rgb(0 17 34 / 35%);

            @media (max-width: ${theme.sz.md}) {
              width: ${theme.sz.s20};
              height: ${theme.sz.s20};
              font-size: ${theme.sz.s10};
            }
          `
        : css`
            background-color: ${theme.co.tertiary90};
            color: ${theme.co.tertiary0};
            font-size: ${theme.sz.s8};
          `
    }
  `,
);

export const PromptText = styled.div<{ $overlay?: boolean }>(
  ({ theme, $overlay }) => css`
    flex: ${$overlay ? '0 1 auto' : 1};
    display: flex;
    flex-direction: column;
    gap: ${$overlay ? theme.sz.s2 : theme.sz.s1};
    min-width: 0;

    ${
      $overlay &&
      css`
        align-items: flex-end;
      `
    }
  `,
);

export const PromptTitle = styled.div<{ $overlay?: boolean }>(
  ({ theme, $overlay }) => css`
    ${
      $overlay
        ? css`
            ${theme.typography.heading.desktop.s};
            color: ${theme.co.neutral99};
            ${overlayTextShadow};

            @media (max-width: ${theme.sz.md}) {
              ${theme.typography.heading.desktop.xxs};
            }
          `
        : css`
            ${theme.typography.heading.desktop.xxs};
            color: ${theme.co.secondary0};
          `
    }
  `,
);

export const PromptDescription = styled.div(
  ({ theme }) => css`
    ${theme.typography.paragraph.s};
    color: ${theme.co.secondary50};
  `,
);

export const AttemptsLeft = styled.div<{ $overlay?: boolean }>(
  ({ theme, $overlay }) => css`
    ${theme.typography.paragraph.s};
    color: ${$overlay ? theme.co.neutral99 : theme.co.secondary50};

    ${
      $overlay &&
      css`
        text-shadow: 0 1px 3px rgb(0 17 34 / 60%);
      `
    }
  `,
);

export const ExpandedContainer = styled.div<{ $overlay?: boolean }>(
  ({ theme, $overlay }) => css`
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: ${$overlay ? 'none' : theme.sz.sm};
    border-radius: ${$overlay ? theme.bo.mediumLarge : theme.bo.medium};
    overflow: hidden;
    background-color: ${theme.co.neutral99};

    ${
      $overlay
        ? css`
            margin-bottom: ${theme.sz.s4};
            padding: ${theme.sz.s5};
            box-shadow: 0 ${theme.sz.s1} ${theme.sz.s6} rgb(0 17 34 / 25%);

            @media (min-width: ${theme.sz.md}) {
              margin-bottom: ${theme.sz.s6};
            }

            @media (max-width: ${theme.sz.md}) {
              padding: ${theme.sz.s4};
            }
          `
        : css`
            box-shadow: 0 ${theme.sz.s1} ${theme.sz.s4} rgb(0 17 34 / 8%);
          `
    }
  `,
);

export const AttemptsFooter = styled.div<{ $overlay?: boolean }>(
  ({ theme, $overlay }) => css`
    flex-shrink: 0;
    width: 100%;
    padding: ${$overlay ? `${theme.sz.s3} 0 0` : `${theme.sz.s3} ${theme.sz.s4} ${theme.sz.s4}`};
    text-align: center;
    color: ${theme.co.secondary50};
    ${theme.typography.paragraph.s};
  `,
);

export const PronunciationIcon = styled(Icon)(
  () => css`
    font-size: 1em;
  `,
);

export const ChevronIcon = styled(Icon)(
  ({ theme }) => css`
    flex-shrink: 0;
    color: ${theme.co.secondary60};
    font-size: ${theme.sz.s6};
  `,
);

export const CloseButton = styled.button(
  ({ theme }) => css`
    position: absolute;
    top: ${theme.sz.s2};
    inset-inline-end: ${theme.sz.s2};
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${theme.sz.s10};
    height: ${theme.sz.s10};
    padding: 0;
    border: none;
    border-radius: ${theme.bo.round};
    background: none;
    color: ${theme.co.secondary60};
    font-size: ${theme.sz.s6};
    cursor: pointer;

    &:hover {
      color: ${theme.co.secondary20};
    }

    &:focus-visible {
      outline: ${theme.bo.mediumWidth} solid ${theme.co.secondary40};
      outline-offset: ${theme.sz.s1};
    }
  `,
);

export const CloseIcon = styled(Icon)(
  () => css`
    font-size: 1em;
  `,
);

export const PillPrompt = styled.button(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.sz.s3};
    width: fit-content;
    max-width: 100%;
    padding: ${theme.sz.s1} ${theme.sz.s5} ${theme.sz.s1} ${theme.sz.s1};
    border: none;
    border-radius: ${theme.bo.round};
    background-color: ${theme.co.primary30};
    color: ${theme.co.secondary0};
    text-align: start;
    cursor: pointer;

    &:hover {
      background-color: ${theme.co.primary20};
    }

    ${theme.focusVisible};
  `,
);

export const PillMicIcon = styled.span(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: ${theme.sz.s10};
    height: ${theme.sz.s10};
    border-radius: ${theme.bo.round};
    background-color: ${theme.co.primary10};
    font-size: ${theme.sz.s5};
  `,
);

export const PillText = styled.span`
  display: flex;
  flex-direction: column;
`;

export const PillTitle = styled.span(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.xxxs};
  `,
);

export const PermissionModal = styled(Modal)(
  ({ theme }) => css`
    .reactModalContent {
      max-width: ${theme.sz.sm};
    }
  `,
);

export const PermissionModalBody = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: ${theme.sz.s16} ${theme.sz.s6} ${theme.sz.s6};

    ${PermissionMicIcon} {
      justify-self: center;
    }

    ${ButtonWrapper} {
      width: 100%;
    }
  `,
);

export const PillAttemptsLeft = styled.span(
  ({ theme }) => css`
    ${theme.typography.paragraph.s};
    color: ${theme.co.secondary30};
  `,
);
